import React, { useEffect, useState } from 'react';
import { Loader2, MailWarning, MailCheck, RefreshCw, Mail } from 'lucide-react';
import api from '@/lib/axios';
import { formatDeNumber } from '@/lib/utils';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import EmailRecipientFields, { emptyRoutingState, routingApiPayload } from '@/components/email/EmailRecipientFields';

const fmtDT = (s) => s
  ? new Date(s).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

export const SepaChargebacksTab = ({ refreshKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [remindingId, setRemindingId] = useState(null);
  const [remindPreview, setRemindPreview] = useState(null);
  const [routing, setRouting] = useState(emptyRoutingState());

  const load = () => {
    setLoading(true);
    api.get('/sepa/chargebacks')
      .then(r => setItems(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [refreshKey]);

  const openRemind = (inv) => {
    if (!inv.email) {
      toast.error('Keine E-Mail-Adresse hinterlegt — bitte Stammdaten prüfen');
      return;
    }
    setRouting(emptyRoutingState());
    setRemindPreview(inv);
  };

  const confirmRemind = async () => {
    const inv = remindPreview;
    if (!inv) return;
    setRemindingId(inv.id);
    try {
      const r = await api.post(`/sepa/chargebacks/${inv.id}/remind`, {
        preview_confirmed: true,
        ...routingApiPayload(routing),
      });
      if (r.data.queued) {
        toast.info(`Zahlungserinnerung zur Freigabe eingereiht — Versand erst nach Freigabe in der E-Mail-Queue`);
      } else {
        toast.success(`Zahlungserinnerung an ${r.data.recipient} gesendet`);
      }
      setRemindPreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setRemindingId(null);
    }
  };

  return (
    <Card>
      <CardContent className="p-0">
        {loading ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /> Lade…</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400 italic" data-testid="chargebacks-empty">
            Keine offenen Rückbuchungen. Rücklastschriften erscheinen, sobald der Kontoauszug von der Bank geholt oder importiert wurde;
            die Stufe-1-Mahnung wird dort automatisch ausgelöst (falls aktiviert).
          </div>
        ) : (
          <Table data-testid="chargebacks-table">
            <TableHeader>
              <TableRow>
                <TableHead>Rechnung</TableHead>
                <TableHead>Familie</TableHead>
                <TableHead className="text-right">Betrag</TableHead>
                <TableHead>Rückbuchung am</TableHead>
                <TableHead>Grund</TableHead>
                <TableHead>Gemahnt</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(inv => (
                <TableRow key={inv.id} data-testid={`chargeback-row-${inv.invoice_number}`}>
                  <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                  <TableCell>{inv.family_name || '—'}</TableCell>
                  <TableCell className="text-right font-semibold">{formatDeNumber(inv.total_amount)} €</TableCell>
                  <TableCell className="text-xs">{fmtDT(inv.chargeback_at)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] border-rose-200 bg-rose-50 text-rose-600">
                      {inv.chargeback_reason || 'Rücklastschrift'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {inv.reminded_at
                      ? <span className="text-emerald-700 inline-flex items-center gap-1"><MailCheck className="w-3.5 h-3.5" />{fmtDT(inv.reminded_at)}</span>
                      : <span className="text-slate-400">nein</span>}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" disabled={remindingId === inv.id}
                            onClick={() => openRemind(inv)}
                            data-testid={`remind-btn-${inv.invoice_number}`}
                            className="border-amber-300 text-amber-700 hover:bg-brand-red/5">
                      {remindingId === inv.id
                        ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        : <MailWarning className="w-3.5 h-3.5 mr-1" />}
                      {inv.reminded_at ? 'Erneut mahnen' : 'Mahnung senden'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div className="p-3 border-t border-slate-100 text-right">
          <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Aktualisieren</Button>
        </div>
      </CardContent>

      <Dialog open={!!remindPreview} onOpenChange={(o) => { if (!o && !remindingId) setRemindPreview(null); }}>
        <DialogContent className="max-w-lg" data-testid="chargeback-remind-dialog">
          <DialogHeader>
            <DialogTitle>Zahlungserinnerung senden</DialogTitle>
            <DialogDescription>
              Rechnung {remindPreview?.invoice_number}
              {remindPreview?.family_name ? ` · ${remindPreview.family_name}` : ''}
              {remindPreview ? ` · ${formatDeNumber(remindPreview.total_amount)} €` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div data-testid="chargeback-remind-to">
              <span className="text-slate-500">An:</span>{' '}
              <span className="font-medium text-slate-800">{remindPreview?.email || '—'}</span>
            </div>
            <EmailRecipientFields
              extraTo={routing.extraTo}
              cc={routing.cc}
              bcc={routing.bcc}
              onChange={setRouting}
              disabled={!!remindingId}
              testId="chargeback-remind-routing"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemindPreview(null)} disabled={!!remindingId}>Abbrechen</Button>
            <Button onClick={confirmRemind} disabled={!!remindingId || !remindPreview?.email} data-testid="chargeback-remind-confirm">
              {remindingId ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              Jetzt senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SepaChargebacksTab;
